import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MenuService } from '../../shared/menu/menu.service';
import { WorkflowCategory, WorkflowStage } from '../../shared/menu/menu.model';
import { MailWorkflowService, MailTaskCounts } from '../../mail-workflow/mail-workflow.service';
import { WfRuntimeSidebarComponent } from '../../wf-runtime/components/wf-runtime-sidebar.component';

/**
 * Round 6 unified sidebar — renders BOTH the legacy (Contract / Mail)
 * categories AND the kernel-driven workflows in a single panel under
 * one "Workflows" header.
 *
 * <h2>Two data sources, one render</h2>
 *
 * <ul>
 *   <li><b>Legacy:</b> {@code MenuService.workflowSidebar$} — emits the
 *       {@link WorkflowCategory}/{@link WorkflowStage} tree built from
 *       {@code /api/menu}. These are the hardcoded CONTRACT and MAIL
 *       workflowTypes from pre-Round-6.</li>
 *   <li><b>Kernel:</b> {@link WfRuntimeSidebarComponent} embedded with
 *       {@code [showHeader]="false"} renders the kernel
 *       {@code RuntimeMenuNode} tree from {@code /api/wf/menu/runtime}.</li>
 * </ul>
 *
 * <p>The two sections are visually separated by a thin divider. The
 * legacy section is on top so existing users see what they're used to
 * first; the kernel section grows underneath as new workflows are
 * deployed.
 *
 * <h2>Migration path</h2>
 *
 * <p>When a legacy workflow (e.g. Contract) is re-implemented as a kernel
 * workflow, it stops appearing in {@code MenuService.workflowSidebar$}
 * (server-side filter or admin toggle) and starts appearing in the kernel
 * tree. No frontend change needed — the same user clicks the same name
 * in the same sidebar, just now powered by the kernel. The legacy section
 * shrinks over time and is eventually empty / removable.
 *
 * <h2>Navigation</h2>
 *
 * <p>Stage clicks use Angular's {@code [routerLink]} directive — no
 * imperative {@code router.navigate} calls. {@code routerLinkActive}
 * applies the legacy {@code .selected} class so the existing CSS is
 * reused without modification. Mail badges live on each row and read
 * from {@link MailWorkflowService}; the subscription stays inside this
 * component because it's the only consumer.
 *
 * <h2>Why ViewEncapsulation defaults are kept</h2>
 *
 * <p>The shell ({@code SkyPlusWorkflowComponent}) uses
 * {@code ViewEncapsulation.None} so its 2,000-line legacy stylesheet
 * becomes global. This sidebar uses Angular's default emulated
 * encapsulation, but the {@code .sp-category-*} / {@code .sp-stage-*}
 * class names match the global rules and apply automatically. The only
 * scoped styles added here are for the divider between legacy and kernel
 * sections, which has no global equivalent.
 */
@Component({
  selector: 'app-workflow-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule, WfRuntimeSidebarComponent],
  template: `
    <div class="sp-workflow-sidebar-inner">

      <!-- Single header for the whole sidebar -->
      <div class="sp-sidebar-header">
        <span class="material-icons">account_tree</span>
        <span>Workflows</span>
      </div>

      <!-- ─── Legacy section: Contract / Mail / future workflowTypes ─── -->
      <div class="sp-workflow-categories" *ngIf="!sidebarLoading && categories.length > 0">
        <div class="sp-category" *ngFor="let category of categories; trackBy: trackByFn">

          <div class="sp-category-header"
               [class.expanded]="category.expanded"
               (click)="toggleCategory(category)">
            <span class="material-icons expand-icon">
              {{ category.expanded ? 'expand_more' : 'chevron_right' }}
            </span>
            <span class="material-icons category-icon" [style.color]="category.color">
              {{ category.icon }}
            </span>
            <span class="category-name">{{ category.name }}</span>
          </div>

          <div class="sp-category-stages" *ngIf="category.expanded">

            <!-- Mail Management — has sub-group dividers ("MY TASKS" / "MY MAIL") -->
            <ng-container *ngIf="category.id === 'mail-tasks'">
              <div class="sp-stage-group-label">MY TASKS</div>
              <ng-container *ngFor="let stage of category.stages; trackBy: trackByFn">
                <!-- Sub-group break before "My Mail" -->
                <div *ngIf="stage.id === 'ml-drafts'" class="sp-stage-group-label">MY MAIL</div>

                <a class="sp-stage-item"
                   [routerLink]="stageLink(stage)"
                   routerLinkActive="selected"
                   [routerLinkActiveOptions]="{ exact: true }">
                  <span class="material-icons stage-icon">{{ stage.icon }}</span>
                  <span class="stage-name">{{ stage.name }}</span>
                  <!-- Live badge for inbox / dispatch / bringup / drafts -->
                  <span *ngIf="mailStageBadge(stage.id) > 0" class="sp-stage-badge">
                    {{ mailStageBadge(stage.id) }}
                  </span>
                </a>
              </ng-container>
            </ng-container>

            <!-- All other categories (contract etc.) — plain list -->
            <ng-container *ngIf="category.id !== 'mail-tasks'">
              <a class="sp-stage-item"
                 *ngFor="let stage of category.stages; trackBy: trackByFn"
                 [routerLink]="stageLink(stage)"
                 routerLinkActive="selected"
                 [routerLinkActiveOptions]="{ exact: true }">
                <span class="material-icons stage-icon">{{ stage.icon }}</span>
                <span class="stage-name">{{ stage.name }}</span>
              </a>
            </ng-container>

          </div>
        </div>
      </div>

      <!-- Loading state for the legacy section -->
      <div class="sp-sidebar-loading" *ngIf="sidebarLoading">
        <span class="material-icons spin">progress_activity</span>
        <span>Loading…</span>
      </div>

      <!-- Divider between legacy and kernel sections.
           Rendered only when there are legacy categories so the kernel
           section can sit flush at the top in fully-migrated tenants. -->
      <div class="sp-sidebar-divider" *ngIf="categories.length > 0">
        <span></span>
      </div>

      <!-- ─── Kernel section: WfRuntimeMenuApi.tree() ─────────────────── -->
      <!-- showHeader=false: the unified header above is the only one we want. -->
      <wf-runtime-sidebar [showHeader]="false"></wf-runtime-sidebar>

    </div>
  `,
  styles: [`
    /* Outer wrapper — the parent <aside> already has a width/border/etc.
       set by the shell. This component just fills it. */
    .sp-workflow-sidebar-inner {
      display: flex;
      flex-direction: column;
      min-height: 100%;
    }

    /* Header — single, unified. Mirrors the original .sp-sidebar-header
       styling but defined here so the component is self-contained. */
    .sp-sidebar-header {
      display: flex; align-items: center; gap: 8px;
      padding: 12px 16px;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.85);
      border-bottom: 0.5px solid rgba(0, 0, 0, 0.08);
    }
    .sp-sidebar-header .material-icons {
      font-size: 18px;
      color: rgba(0, 0, 0, 0.55);
    }

    /* Loading hint for the legacy section. */
    .sp-sidebar-loading {
      display: flex; align-items: center; gap: 8px;
      padding: 14px 16px;
      color: rgba(0, 0, 0, 0.45);
      font-size: 12px;
    }
    .sp-sidebar-loading .material-icons { font-size: 16px; }
    .spin { animation: ws-spin 1s linear infinite; }
    @keyframes ws-spin {
      from { transform: rotate(0); }
      to   { transform: rotate(360deg); }
    }

    /* Divider between the legacy and kernel sections. Subtle so it
       reads as a section break, not a hard wall. */
    .sp-sidebar-divider {
      padding: 6px 16px;
    }
    .sp-sidebar-divider > span {
      display: block;
      height: 1px;
      background: rgba(0, 0, 0, 0.08);
    }
  `]
})
export class WorkflowSidebarComponent implements OnInit, OnDestroy {

  /** Legacy categories (Contract, Mail) loaded from MenuService. */
  categories: WorkflowCategory[] = [];

  /** True until the first MenuService emission. */
  sidebarLoading = true;

  /** Live mail badge counts (DISPATCH / INBOX / BRINGUP / DRAFTS). */
  mailCounts: MailTaskCounts = { DISPATCH: 0, INBOX: 0, BRINGUP: 0, DRAFTS: 0 };

  private readonly destroy$ = new Subject<void>();

  constructor(
    private menuService: MenuService,
    private mailService: MailWorkflowService
  ) {}

  ngOnInit(): void {
    // Legacy sidebar categories.
    this.menuService.workflowSidebar$
      .pipe(takeUntil(this.destroy$))
      .subscribe(categories => {
        this.categories = categories;
        this.sidebarLoading = !this.menuService.loaded$.value;
        // Auto-expand the first category so the user immediately sees
        // its stages on first load — same default as pre-Round-6.
        if (categories.length > 0 && categories[0]) {
          categories[0].expanded = true;
        }
      });

    // Live mail badge counts.
    this.mailService.taskCounts$
      .pipe(takeUntil(this.destroy$))
      .subscribe(c => this.mailCounts = c);
    this.mailService.refreshTaskCounts();

    // Trigger menu load if not yet loaded (no-op when cached).
    this.menuService.loadMenu();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Sidebar interactions ───────────────────────────────────────────────

  toggleCategory(category: WorkflowCategory): void {
    category.expanded = !category.expanded;
  }

  /**
   * Resolve the routerLink for a stage based on its workflowType. Returns
   * an array suitable for {@code [routerLink]} binding. Unknown types
   * route to /workflow (empty landing) so the click doesn't 404; in
   * practice this should never happen — every {@link WorkflowStage} the
   * backend emits has a known workflowType.
   */
  stageLink(stage: WorkflowStage): string[] {
    const wt = (stage.workflowType ?? '').toUpperCase();
    if (wt === 'CONTRACT') return ['/workflow', 'contract', stage.id];
    if (wt === 'MAIL')     return ['/workflow', 'mail',     stage.id];
    return ['/workflow'];
  }

  /**
   * Live count badge for mail stages. Returns 0 for any stage that
   * doesn't have a tracked count — the template's {@code *ngIf > 0}
   * guard prevents an empty pill from rendering.
   */
  mailStageBadge(stageId: string): number {
    const map: Record<string, number> = {
      'ml-dispatch': this.mailCounts.DISPATCH,
      'ml-inbox':    this.mailCounts.INBOX,
      'ml-bringup':  this.mailCounts.BRINGUP,
      'ml-drafts':   this.mailCounts.DRAFTS
    };
    return map[stageId] || 0;
  }

  trackByFn(_: number, item: { id: string }): string {
    return item.id;
  }
}